import { useEffect, useMemo, useRef } from "react";
import type { Coordinate } from "@/entities/tracking";
import {
  bearingBetween,
  headingAtDistanceM,
  measurePolyline,
  metersBetween,
  pointAtDistanceM,
  projectOntoPolyline,
  type MeasuredPolyline,
} from "@/shared/geo/polyline";

// Déplacement de la Main sur la carte — LE LONG DE LA RUE, pas à travers.
//
// ## Le défaut que ce moteur corrige
// La version précédente interpolait en ligne droite entre deux positions GPS
// successives. C'était déjà mieux que rien : sans elle, le marqueur se
// téléportait d'un ping à l'autre.
//
// Mais interpoler entre A et B, c'est par construction parcourir le SEGMENT
// [A,B] — une ligne droite. Entre deux pings espacés de quelques secondes, la
// moto coupait donc les virages, traversait les pâtés de maisons, et retrouvait
// la rue au ping suivant. Le client voyait exactement ce qu'il a rapporté : une
// ligne droite qui avance, là où le bouton « Itinéraire » lui montre, dans
// Google Maps, un trajet qui épouse les rues.
//
// Lisser le saut ne suffisait pas. Il fallait changer de repère.
//
// ## Le repère : l'abscisse curviligne
// Une position n'est plus traitée comme un couple lat/lng mais comme un NOMBRE
// DE MÈTRES DEPUIS LE DÉPART DU TRACÉ. Dans ce repère, avancer c'est augmenter
// ce nombre — et tout point qu'on en déduit est, par définition, sur le bitume.
// Le marqueur ne peut plus couper : la géométrie le lui interdit.
//
// Trois choses en découlent gratuitement :
//   • la moto prend les virages, puisqu'elle suit les sommets du tracé ;
//   • le CAP est la tangente à la route, et non la corde entre deux pings — la
//     flèche tourne dans le virage, avec lui, au lieu de pointer à travers ;
//   • on sait quelle PART du trajet est faite, donc on peut dessiner
//     séparément le parcouru et le restant.
//
// ## Pourquoi impératif, sans état React
// Ce moteur émet une frame soixante fois par seconde. Passer par `useState`
// re-rendrait l'écran de suivi à cette cadence, avec tous ses effets à
// re-comparer — pour ne changer, au bout du compte, qu'un attribut de marqueur.
// On appelle donc un callback qui pilote le SDK directement. Le composant React
// ne se re-rend pas une seule fois pendant l'animation.

/**
 * Écart maximal entre la position GPS et le tracé pour qu'on RECALE la Main
 * dessus.
 *
 * 80 m : au-delà, elle n'est plus « sur cette rue avec du bruit de mesure »,
 * elle est ailleurs — et la recaler serait mentir sur sa position. On retombe
 * alors sur l'interpolation directe, en attendant que le tracé soit recalculé
 * (`OFF_ROUTE_THRESHOLD_M`, 150 m, s'en charge en amont).
 *
 * Le seuil de recalage est volontairement PLUS SERRÉ que celui du recalcul :
 * entre les deux, la Main garde sa position réelle sans qu'on redemande un
 * itinéraire à chaque imprécision.
 */
const SNAP_MAX_OFFSET_M = 80;

/**
 * Recul toléré sans y croire.
 *
 * Le bruit GPS fait osciller la projection de quelques mètres autour de la
 * position vraie. Rejouer ces oscillations ferait vibrer le marqueur d'avant en
 * arrière à l'arrêt. En deçà de ce seuil, on ne recule pas ; au-delà, c'est un
 * vrai demi-tour et on le suit.
 */
const BACKWARD_TOLERANCE_M = 25;

/** Durée d'interpolation, bornée : trop court ça saute, trop long ça traîne. */
const MIN_DURATION_MS = 400;
const MAX_DURATION_MS = 6_000;

/** Glissement court quand c'est le TRACÉ qui change, pas la position. */
const REROUTE_SLIDE_MS = 500;

/** En deçà, le déplacement relève du bruit GPS — on ne tourne pas l'icône. */
const MIN_HEADING_DISTANCE_M = 3;

export type FollowerFrame = {
  /** Position à afficher — sur le tracé quand la Main y est recalée. */
  coord: Coordinate;
  /** Cap en degrés (0 = nord, sens horaire), ou `null` tant qu'inconnu. */
  heading: number | null;
  /**
   * Abscisse curviligne sur le tracé, en mètres, ou `null` quand la Main n'y
   * est pas recalée. C'est elle qui permet de séparer parcouru et restant.
   */
  alongM: number | null;
  /** Le tracé mesuré auquel `alongM` se rapporte (`null` s'il n'y en a pas). */
  measured: MeasuredPolyline | null;
};

/**
 * Signature légère d'un tracé.
 *
 * Les écrans reconstruisent parfois le tableau `routePoints` à l'identique
 * (nouveau `.map()`, même contenu). Comparer les références relancerait alors
 * une animation de recalage pour rien, à chaque rendu. On compare donc ce qui
 * distingue réellement deux itinéraires : leur longueur et leurs extrémités.
 */
function routeSignature(points: Coordinate[]): string {
  if (points.length === 0) return "0";
  const a = points[0];
  const b = points[points.length - 1];
  return `${points.length}:${a.latitude.toFixed(5)},${a.longitude.toFixed(5)}:${b.latitude.toFixed(5)},${b.longitude.toFixed(5)}`;
}

/**
 * Anime la Main le long de `routePoints` et appelle `onFrame` à chaque frame.
 *
 * `onFrame` n'a pas besoin d'être stable : il est lu via une ref, donc le
 * changer ne relance aucune animation.
 */
export function useRouteFollower(
  target: Coordinate | null | undefined,
  routePoints: Coordinate[],
  onFrame: (frame: FollowerFrame) => void,
): void {
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;

  const signature = routeSignature(routePoints);
  // Mesuré une seule fois par itinéraire : l'animation le relit soixante fois
  // par seconde, mais par dichotomie — jamais en le reparcourant.
  const measured = useMemo(
    () => (routePoints.length >= 2 ? measurePolyline(routePoints) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature],
  );

  const measuredRef = useRef<MeasuredPolyline | null>(measured);
  measuredRef.current = measured;

  // ── État de l'animation, en refs : le faire vivre dans l'état React
  // relancerait un rendu par frame, ce que ce moteur existe pour éviter. ──────
  const renderedRef = useRef<Coordinate | null>(null);
  const alongRef = useRef<number | null>(null);
  const headingRef = useRef<number | null>(null);
  const frameRef = useRef<number | null>(null);
  const lastPingAtRef = useRef<number | null>(null);

  type Anim =
    | { mode: "route"; fromAlong: number; toAlong: number; startedAt: number; duration: number }
    | {
        mode: "direct";
        from: Coordinate;
        to: Coordinate;
        startedAt: number;
        duration: number;
      };
  const animRef = useRef<Anim | null>(null);

  const emit = useRef((coord: Coordinate) => {
    renderedRef.current = coord;
    onFrameRef.current({
      coord,
      heading: headingRef.current,
      alongM: alongRef.current,
      measured: measuredRef.current,
    });
  }).current;

  /**
   * Recalcule l'animation en cours vers `to`.
   *
   * Appelé sur nouvelle position GPS, et sur changement d'itinéraire — dans ce
   * second cas la Main n'a pas bougé, mais le repère dans lequel on la situe,
   * lui, a changé : il faut la re-projeter sur le nouveau tracé.
   */
  const retarget = useRef((to: Coordinate, durationMs: number) => {
    const m = measuredRef.current;
    const previous = renderedRef.current;

    // Première position connue : on la pose, sans animer. Animer depuis un
    // point arbitraire ferait traverser la carte à la moto.
    if (!previous) {
      const proj = projectOntoPolyline(to, m);
      const snapped = proj && proj.offsetM <= SNAP_MAX_OFFSET_M;
      alongRef.current = snapped ? proj.alongM : null;
      headingRef.current = snapped ? headingAtDistanceM(m, proj.alongM) : null;
      emit(snapped ? proj.point : to);
      return;
    }

    const targetProj = projectOntoPolyline(to, m);
    const fromProj = projectOntoPolyline(previous, m);

    const canFollowRoute =
      targetProj != null &&
      fromProj != null &&
      targetProj.offsetM <= SNAP_MAX_OFFSET_M &&
      fromProj.offsetM <= SNAP_MAX_OFFSET_M;

    if (canFollowRoute) {
      const fromAlong = fromProj.alongM;
      let toAlong = targetProj.alongM;

      // Recul minime = bruit du capteur, pas un demi-tour. Le laisser passer
      // ferait vibrer le marqueur d'avant en arrière, moto à l'arrêt.
      if (toAlong < fromAlong && fromAlong - toAlong < BACKWARD_TOLERANCE_M) {
        toAlong = fromAlong;
      }
      animRef.current = {
        mode: "route",
        fromAlong,
        toAlong,
        startedAt: performance.now(),
        duration: durationMs,
      };
    } else {
      // Hors tracé : on reste honnête sur la position réelle. L'interpolation
      // directe reprend, en attendant qu'un nouvel itinéraire soit calculé.
      alongRef.current = null;
      animRef.current = {
        mode: "direct",
        from: previous,
        to,
        startedAt: performance.now(),
        duration: durationMs,
      };

      // Cap : la corde entre les deux points, faute de tracé où lire une
      // tangente. En deçà du bruit GPS on garde le précédent — sinon l'icône
      // pivoterait sur place, moto à l'arrêt.
      if (metersBetween(previous, to) > MIN_HEADING_DISTANCE_M) {
        headingRef.current = bearingBetween(previous, to);
      }
    }

    // Rien à animer : la Main n'a pas bougé (heartbeat, feu rouge, achats en
    // cours). Lancer une boucle de frames pour recalculer soixante fois par
    // seconde une position identique réveillerait le processeur — et la
    // batterie — pour rien.
    const anim = animRef.current;
    const still =
      anim.mode === "route"
        ? anim.toAlong === anim.fromAlong
        : anim.from.latitude === anim.to.latitude && anim.from.longitude === anim.to.longitude;
    if (still) {
      if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      animRef.current = null;
      if (anim.mode === "route") {
        // Poser l'abscisse même sans animer : c'est elle qui découpe le tracé
        // en parcouru / restant, et un changement d'itinéraire à l'arrêt doit
        // redessiner cette coupure.
        alongRef.current = anim.toAlong;
        emit(pointAtDistanceM(m, anim.toAlong) ?? to);
      } else {
        emit(to);
      }
      return;
    }

    const step = () => {
      const anim = animRef.current;
      if (!anim) return;

      const t = Math.min((performance.now() - anim.startedAt) / anim.duration, 1);

      if (anim.mode === "route") {
        const along = anim.fromAlong + (anim.toAlong - anim.fromAlong) * t;
        alongRef.current = along;
        const point = pointAtDistanceM(measuredRef.current, along);
        if (point) {
          // Cap TANGENT à la route : l'icône s'aligne sur la rue, et tourne
          // dans le virage au lieu de pointer à travers.
          if (Math.abs(anim.toAlong - anim.fromAlong) > MIN_HEADING_DISTANCE_M) {
            const h = headingAtDistanceM(measuredRef.current, along);
            if (h != null) headingRef.current = h;
          }
          emit(point);
        }
      } else {
        const coord = {
          latitude: anim.from.latitude + (anim.to.latitude - anim.from.latitude) * t,
          longitude: anim.from.longitude + (anim.to.longitude - anim.from.longitude) * t,
        };
        emit(coord);
      }

      if (t < 1) {
        frameRef.current = requestAnimationFrame(step);
      } else {
        frameRef.current = null;
        animRef.current = null;
      }
    };

    if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(step);
  }).current;

  // ── Nouvelle position GPS ────────────────────────────────────────────────
  const lat = target?.latitude;
  const lng = target?.longitude;
  useEffect(() => {
    if (lat == null || lng == null) return;

    const now = performance.now();
    // Durée = intervalle RÉEL entre les deux derniers pings. Le marqueur met
    // donc le même temps à parcourir son segment que la moto dans la rue — ni
    // en avance, ni à la traîne. Borné : le socket peut hoqueter (réseau,
    // onglet mis en veille), et il ne faut ni figer le marqueur plusieurs
    // secondes ni le faire filer d'un coup.
    const observed = lastPingAtRef.current != null ? now - lastPingAtRef.current : MIN_DURATION_MS;
    lastPingAtRef.current = now;

    retarget(
      { latitude: lat, longitude: lng },
      Math.min(Math.max(observed, MIN_DURATION_MS), MAX_DURATION_MS),
    );
  }, [lat, lng, retarget]);

  // ── Nouvel itinéraire ────────────────────────────────────────────────────
  // La Main n'a pas bougé, mais le tracé sur lequel on la situe a changé : son
  // abscisse sur l'ancien n'a plus de sens. On la re-projette, en glissant
  // brièvement pour que le recalage ne se lise pas comme un saut.
  const appliedSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    const first = appliedSignatureRef.current == null;
    appliedSignatureRef.current = signature;
    // Au premier rendu, l'effet de position vient déjà de poser la Main : la
    // recaler une seconde fois ne ferait que lancer une animation vide.
    if (first) return;
    if (lat == null || lng == null) return;
    if (!renderedRef.current) return;
    retarget({ latitude: lat, longitude: lng }, REROUTE_SLIDE_MS);
    // `lat`/`lng` volontairement hors dépendances : cet effet ne doit répondre
    // QU'au changement d'itinéraire. L'effet ci-dessus couvre les positions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // ── Arrêt ────────────────────────────────────────────────────────────────
  useEffect(
    () => () => {
      if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      animRef.current = null;
    },
    [],
  );
}
